import React, { useEffect, useState } from "react";
import {
  drawingCoordinateFields,
  drawingCoordinatePatch,
} from "./chartDrawingCoordinates";

export default function ChartDrawingCoordinates({
  overlay,
  tickSize,
  onChange,
  palette,
  busy,
  compact = false,
}) {
  const fields = drawingCoordinateFields(overlay);
  const signature = JSON.stringify(fields);
  const [values, setValues] = useState(() =>
    Object.fromEntries(
      fields.map((field) => [field.key, String(field.value ?? "")]),
    ),
  );
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const reset = () => {
    setValues(
      Object.fromEntries(
        fields.map((field) => [field.key, String(field.value ?? "")]),
      ),
    );
    setError(null);
  };
  useEffect(() => {
    const next = JSON.parse(signature);
    setValues(
      Object.fromEntries(
        next.map((field) => [field.key, String(field.value ?? "")]),
      ),
    );
    setError(null);
  }, [overlay?.id, signature]);
  if (!fields.length) return null;
  const dirty = fields.some(
    (field) => values[field.key] !== String(field.value ?? ""),
  );
  const disabled = busy || saving || overlay.locked;
  const submit = async (event) => {
    event.preventDefault();
    if (disabled || !dirty) return;
    const result = drawingCoordinatePatch(overlay, values, tickSize);
    if (result.error) {
      setError(result.error);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await onChange(result.patch);
      if (saved === false || saved?.ok === false)
        setError(saved?.error || "Coordinates could not be saved. Try again.");
    } catch (_error) {
      setError("Coordinates could not be saved. Try again.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      data-chart-drawing-coordinates
      onSubmit={submit}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") {
          event.preventDefault();
          reset();
        }
      }}
      style={{
        display: "flex",
        flexDirection: compact ? "row" : "column",
        gap: 8,
        alignItems: compact ? "center" : "stretch",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        {fields.map((field) => (
          <label
            key={field.key}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 0,
              gridColumn: field.type ? "span 2" : undefined,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: palette.mutedText,
                display: compact ? "none" : undefined,
              }}
            >
              {field.label}
            </span>
            <input
              aria-label={`Drawing ${field.label.toLowerCase()}`}
              data-chart-drawing-coordinate={field.key}
              type={field.type || "text"}
              inputMode={field.type ? undefined : "decimal"}
              step={field.type ? 1 : undefined}
              value={values[field.key] ?? ""}
              disabled={disabled}
              onChange={(event) => {
                setValues({ ...values, [field.key]: event.target.value });
                setError(null);
              }}
              style={{
                width: compact ? 100 : "100%",
                minWidth: 0,
                boxSizing: "border-box",
                height: 34,
                padding: "0 8px",
                borderRadius: 7,
                border: `1px solid ${error ? "#f87171" : palette.border}`,
                background: "transparent",
                color: palette.text,
                font: "inherit",
                fontSize: 12,
              }}
            />
          </label>
        ))}
      </div>
      {!compact && tickSize > 0 ? (
        <span style={{ fontSize: 11, color: palette.mutedText }}>
          Price increment: {tickSize}
        </span>
      ) : null}
      {!compact || dirty ? (
        <button
          type="submit"
          data-chart-drawing-coordinate-save
          disabled={disabled || !dirty}
          style={{
            minHeight: 34,
            border: `1px solid ${palette.border}`,
            borderRadius: 7,
            color: palette.text,
            background: "transparent",
            cursor: "pointer",
            font: "inherit",
            fontSize: 12,
          }}
        >
          {saving ? "Saving…" : compact ? "Save" : "Apply coordinates"}
        </button>
      ) : null}
      {error ? (
        <span
          role="alert"
          data-chart-drawing-coordinate-error
          style={{
            fontSize: 11,
            color: "#ef4444",
            ...(compact
              ? {
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  padding: 8,
                  background: palette.background,
                  width: 210,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 8,
                  zIndex: 2,
                }
              : {}),
          }}
        >
          {error}
        </span>
      ) : null}
    </form>
  );
}
